import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import API from "@/lib/api";
import {
  Target, Pencil, Wrench, ClipboardCheck, Gauge,
  Loader2, ArrowRight, Library, Zap, CheckCircle2, TrendingUp
} from "lucide-react";

const MODES = [
  { id: "define",    label: "Define",    icon: Target,         desc: "Scope, requirements, boundaries" },
  { id: "shape",     label: "Shape",     icon: Pencil,         desc: "Architecture, UX, data models" },
  { id: "implement", label: "Implement", icon: Wrench,         desc: "Code, APIs, delivery slices" },
  { id: "verify",    label: "Verify",    icon: ClipboardCheck, desc: "Review + QA, fused" },
  { id: "iterate",   label: "Iterate",   icon: Gauge,          desc: "Optimize + scenarios, fused" },
];

// Top 12 most-common categories — shown in the matrix. Rest are collapsed into "Other".
const MATRIX_CATEGORIES = [
  "Product", "UX", "UI", "Frontend", "Backend", "Business Logic",
  "Data", "API", "Authentication & Authorization", "Security",
  "Testing", "Performance",
];

export function CoverageMap({ concept, projectId, onPullAndGenerate, onSkip }) {
  const [coverage, setCoverage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pulling, setPulling] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedModes, setSelectedModes] = useState(new Set(MODES.map((m) => m.id)));
  const [showMatrix, setShowMatrix] = useState(false);
  const navigate = useNavigate();

  const openSuggest = (modeId, category) => {
    const p = new URLSearchParams({ action: "suggest" });
    if (modeId) p.set("mode", modeId);
    if (category) p.set("category", category);
    navigate(`/library?${p.toString()}`);
  };

  useEffect(() => {
    loadCoverage();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCoverage = async () => {
    try {
      const { data } = await API.get(`/library/coverage`);
      setCoverage(data);
    } catch (e) {
      console.error("Coverage load error:", e);
      setCoverage({ modes: {}, total: 0 });
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = (modeId) => {
    setSelectedModes((prev) => {
      const next = new Set(prev);
      if (next.has(modeId)) next.delete(modeId);
      else next.add(modeId);
      return next;
    });
  };

  const totalLibraryBlocks = coverage ? Object.values(coverage.modes).reduce((a, b) => a + b, 0) : 0;
  const coveredModes = coverage ? MODES.filter((m) => (coverage.modes[m.id] || 0) > 0).length : 0;
  const gapModes = MODES.filter((m) => !coverage?.modes[m.id]);
  const selectedGaps = MODES.filter((m) => selectedModes.has(m.id) && !(coverage?.modes[m.id] > 0));
  const selectedPulls = MODES.filter((m) => selectedModes.has(m.id) && (coverage?.modes[m.id] || 0) > 0);

  const handlePullAndGenerate = () => {
    onPullAndGenerate({
      pullModes: selectedPulls.map((m) => m.id),
      generateModes: selectedGaps.map((m) => m.id),
      allSelected: [...selectedModes],
    });
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto border border-[var(--pf-border)] p-12 text-center" data-testid="coverage-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--pf-accent)] mx-auto mb-3" />
        <p className="text-sm text-[var(--pf-text-secondary)]">Analyzing your library coverage...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto" data-testid="coverage-map">
      {/* Concept */}
      <div className="border border-[var(--pf-border)] bg-[var(--pf-surface)] px-5 py-4 mb-6">
        <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[var(--pf-text-muted)] mb-1">Your Concept</p>
        <p className="text-sm text-[var(--pf-text)] font-mono">{concept}</p>
      </div>

      {/* Coverage summary */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading text-lg font-bold text-[var(--pf-text)]">Coverage Map</h2>
          <p className="text-xs text-[var(--pf-text-secondary)] mt-0.5">
            Your library has <strong>{totalLibraryBlocks} blocks</strong> across <strong>{coveredModes}/5 phases</strong>.
            {gapModes.length > 0 ? ` ${gapModes.length} mode${gapModes.length > 1 ? "s" : ""} need${gapModes.length === 1 ? "s" : ""} generation.` : " Full coverage!"}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowMatrix((v) => !v)}
            className="text-[11px] text-[var(--pf-text-muted)] hover:text-[var(--pf-accent)] font-medium"
            data-testid="coverage-toggle-matrix"
          >
            {showMatrix ? "Hide matrix" : "Show mode × category matrix"}
          </button>
          <button
            onClick={() => setSelectedModes(selectedModes.size === MODES.length ? new Set() : new Set(MODES.map((m) => m.id)))}
            className="text-[11px] text-[var(--pf-accent)] hover:text-[var(--pf-accent-hover)] font-medium"
            data-testid="coverage-toggle-all"
          >
            {selectedModes.size === MODES.length ? "Deselect all" : "Select all"}
          </button>
        </div>
      </div>

      {/* Mode × Category matrix (heatmap) */}
      {showMatrix && coverage?.matrix && (
        <div className="border border-[var(--pf-border)] bg-[var(--pf-bg)] p-4 mb-4 overflow-x-auto" data-testid="coverage-matrix">
          <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
            <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[var(--pf-text-muted)]">Mode × Category coverage</p>
            <div className="flex items-center gap-3 text-[10px] text-[var(--pf-text-muted)]">
              <span className="flex items-center gap-1"><TrendingUp className="w-3 h-3 text-[#16A34A]" />= added in last 7d</span>
              <span>Click any cell to suggest additions.</span>
            </div>
          </div>
          <table className="w-full text-[10px] font-mono border-collapse">
            <thead>
              <tr>
                <th className="text-left text-[var(--pf-text-muted)] font-normal py-1 pr-2 sticky left-0 bg-[var(--pf-bg)]"></th>
                {MATRIX_CATEGORIES.map((cat) => (
                  <th key={cat} className="text-[10px] text-[var(--pf-text-muted)] font-normal p-1 text-center align-bottom" title={cat}>
                    <div className="whitespace-nowrap [writing-mode:vertical-rl] rotate-180 h-20 mx-auto">{cat}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MODES.map((mode) => {
                const row = coverage.matrix[mode.id] || {};
                return (
                  <tr key={mode.id}>
                    <td className="text-[11px] font-semibold text-[var(--pf-text)] pr-2 py-0.5 sticky left-0 bg-[var(--pf-bg)]">{mode.label}</td>
                    {MATRIX_CATEGORIES.map((cat) => {
                      const count = row[cat] || 0;
                      const recent = (coverage.matrix_trend?.[mode.id]?.[cat]) || 0;
                      const intensity = count === 0 ? 0 : Math.min(1, count / 5);
                      const bg = count === 0
                        ? "transparent"
                        : `rgba(var(--pf-accent-rgb, 37, 99, 235), ${0.15 + intensity * 0.65})`;
                      const isSparse = count < 2;
                      return (
                        <td key={cat} className="p-0.5 relative">
                          <button
                            onClick={() => openSuggest(mode.id, cat)}
                            className={`w-full h-6 flex items-center justify-center gap-0.5 transition-all hover:outline hover:outline-2 hover:outline-[var(--pf-accent)] ${count > 0 ? "text-white" : "text-[var(--pf-text-muted)] border border-dashed border-[var(--pf-border)]"}`}
                            style={{ backgroundColor: bg }}
                            title={`${mode.label} × ${cat}: ${count} block${count !== 1 ? "s" : ""}${recent > 0 ? ` (+${recent} in last 7d)` : ""}${isSparse ? " — click to suggest additions" : " — click to add more"}`}
                            data-testid={`matrix-cell-${mode.id}-${cat.toLowerCase().replace(/\s/g, "-")}`}
                          >
                            <span>{count > 0 ? count : "·"}</span>
                            {recent > 0 && (
                              <TrendingUp className={`w-2.5 h-2.5 ${count > 0 ? "text-white opacity-90" : "text-[#16A34A]"}`} data-testid={`matrix-trend-${mode.id}-${cat.toLowerCase().replace(/\s/g, "-")}`} />
                            )}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Mode pipeline */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="coverage-modes">
        {MODES.map((mode) => {
          const count = coverage?.modes[mode.id] || 0;
          const hasCoverage = count > 0;
          const isSelected = selectedModes.has(mode.id);
          const Icon = mode.icon;

          return (
            <button
              key={mode.id}
              onClick={() => toggleMode(mode.id)}
              data-testid={`coverage-mode-${mode.id}`}
              className={`text-left border p-4 transition-all duration-150 ${
                isSelected
                  ? hasCoverage
                    ? "border-[#16A34A] bg-[#16A34A]/5"
                    : "border-[var(--pf-accent)] bg-[var(--pf-accent)]/5"
                  : "border-[var(--pf-border)] bg-[var(--pf-bg)] opacity-50"
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Icon className={`w-4 h-4 ${isSelected ? (hasCoverage ? "text-[#16A34A]" : "text-[var(--pf-accent)]") : "text-[var(--pf-text-muted)]"}`} />
                  <span className="text-sm font-semibold text-[var(--pf-text)]">{mode.label}</span>
                </div>
                {isSelected && (
                  <CheckCircle2 className={`w-4 h-4 ${hasCoverage ? "text-[#16A34A]" : "text-[var(--pf-accent)]"}`} />
                )}
              </div>
              <p className="text-[10px] text-[var(--pf-text-muted)] mb-2">{mode.desc}</p>
              {hasCoverage ? (
                <div className="flex items-center gap-1.5">
                  <Library className="w-3 h-3 text-[#16A34A]" />
                  <span className="text-[11px] font-medium text-[#16A34A]">{count} library block{count !== 1 ? "s" : ""}</span>
                </div>
              ) : (
                <div className="flex items-center gap-1.5">
                  <Zap className="w-3 h-3 text-[var(--pf-accent)]" />
                  <span className="text-[11px] font-medium text-[var(--pf-accent)]">Will generate</span>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Action summary */}
      <div className="border border-[var(--pf-border)] bg-[var(--pf-bg)] p-5 mb-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-[var(--pf-text)]">
              {selectedModes.size === 0 ? "Select modes to continue" : (
                <>
                  {selectedPulls.length > 0 && (
                    <span className="text-[#16A34A]">Pull {selectedPulls.reduce((sum, m) => sum + (coverage?.modes[m.id] || 0), 0)} blocks from library</span>
                  )}
                  {selectedPulls.length > 0 && selectedGaps.length > 0 && <span className="text-[var(--pf-text-muted)]"> + </span>}
                  {selectedGaps.length > 0 && (
                    <span className="text-[var(--pf-accent)]">Generate new blocks for {selectedGaps.length} mode{selectedGaps.length > 1 ? "s" : ""}</span>
                  )}
                </>
              )}
            </p>
            {selectedModes.size > 0 && (
              <p className="text-[10px] text-[var(--pf-text-muted)] mt-0.5">
                Library blocks will be pulled with {"{variables}"} — use Contextualize to fill them with your project details.
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onSkip} data-testid="coverage-skip"
              className="rounded-none border-[var(--pf-border)] h-9 px-4 text-xs hover:border-[var(--pf-accent)]">
              Skip
            </Button>
            <Button onClick={handlePullAndGenerate} disabled={selectedModes.size === 0 || pulling || generating} data-testid="coverage-go-button"
              className="rounded-none bg-[var(--pf-accent)] hover:bg-[var(--pf-accent-hover)] text-white h-9 px-5 text-xs font-medium">
              {pulling || generating ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Working...</> : <><ArrowRight className="w-3.5 h-3.5 mr-1.5" />Build Project</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
